import { Check, Info, Minus, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { dismissToast, toastAction, useToast, type Toast } from '../lib/toast';

const TONE_ICON: Record<NonNullable<Toast['tone']>, { icon: typeof Check; color: string }> = {
  done: { icon: Check, color: 'var(--done)' },
  missed: { icon: X, color: 'var(--missed)' },
  skipped: { icon: Minus, color: 'var(--skip)' },
  neutral: { icon: Info, color: 'var(--skip)' },
};

export function Toaster() {
  const toast = useToast();
  const tone = toast?.tone ? TONE_ICON[toast.tone] : null;
  return (
    <div
      aria-live="polite"
      className="toaster pointer-events-none fixed inset-x-0 z-50 flex justify-center px-4 lg:left-60 lg:justify-start lg:px-8"
    >
      {/* One toast at a time: the old one leaves before the new one (and its button) appears. */}
      <AnimatePresence mode="wait">
        {toast && (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, transition: { duration: 0.12 } }}
            transition={{ type: 'spring', stiffness: 500, damping: 36 }}
            className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl bg-inverse py-2 pr-2 pl-4 text-on-inverse shadow-[var(--shadow-float)]"
          >
            {tone && (
              <span
                className="grid h-6 w-6 shrink-0 place-items-center rounded-full"
                style={{ background: tone.color }}
                aria-hidden
              >
                <tone.icon size={14} strokeWidth={3} color="var(--on-status)" />
              </span>
            )}
            <p className="flex-1 py-2 text-[15px] leading-snug">{toast.message}</p>
            {toast.actionLabel ? (
              <button
                type="button"
                onClick={toastAction}
                className="tap min-h-11 rounded-xl px-3 text-[15px] font-semibold"
                style={{ color: 'var(--inverse-accent)' }}
              >
                {toast.actionLabel}
              </button>
            ) : (
              <button
                type="button"
                onClick={dismissToast}
                aria-label="Zapri"
                className="tap grid min-h-11 min-w-11 place-items-center opacity-70"
              >
                <X size={18} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
